import React, { Fragment } from "react";
import { Form, Select, SelectProps } from "antd";
import {
  Controller,
  Control,
  FieldValues,
  PathValue,
  Path,
} from "react-hook-form";
import { InfoIconOutlined, SelectArrow, TickIcon } from "../../CustomIcons";

const { Option } = Select;

interface SelectFieldProps<T extends FieldValues> {
  fieldName: Path<T>;
  label?: string;
  control: Control<T>;
  defaultValue?: PathValue<T, Path<T>> | undefined;
  iProps?: SelectProps<string | number | readonly string[]>;
  isRequired?: boolean;
  rules?: object;
  validate?: "" | "success" | "warning" | "error" | "validating" | undefined;
  validMessage?: string | React.ReactNode;
  className?: string;
  prefix?: React.ReactNode;
  tooltipTitle?: string | React.ReactNode; // Use ReactNode for tooltip title
  optionalLabel?: string;
  hintMessage?: string;
  warning?: boolean;
  onSelectChange?: (
    value: string | number | readonly string[],
    label: string
  ) => void;
  selectOption: { label: string; value: string }[];
}

export const SelectField = <T extends FieldValues>({
  fieldName,
  isRequired = false, // Provide default value
  label,
  validMessage,
  className,
  control,
  defaultValue, // Provide default value
  rules,
  iProps,
  prefix,
  tooltipTitle,
  optionalLabel,
  hintMessage,
  warning,
  validate,
  onSelectChange,
  selectOption,
}: SelectFieldProps<T>) => {
  return (
    <Form.Item
      required={isRequired}
      label={<div className="label_text">{label}</div>}
      validateStatus={validate}
      help={validMessage}
      className={className}
      tooltip={
        tooltipTitle && {
          title: tooltipTitle,
          placement: "topLeft",
          arrow: false,
          icon: <InfoIconOutlined />,
        }
      }
    >
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              suffixIcon={<SelectArrow />}
              {...(iProps && { ...iProps, size: undefined })}
              value={value ?? null}
              menuItemSelectedIcon={<TickIcon style={{ color: "#00A07D" }} />}
              showSearch
              optionFilterProp="label"
              prefix={prefix}
              onChange={(val) => {
                onChange(val);
                if (onSelectChange) {
                  const selectedOption = selectOption?.find((opt) =>
                    Array.isArray(val)
                      ? val?.includes(opt.value)
                      : opt.value === val
                  );

                  if (selectedOption) {
                    onSelectChange(val, selectedOption.label);
                  }
                }
              }}
            >
              {selectOption?.length > 0 &&
                selectOption?.map(
                  (resp: { label: string; value: string }, i: number) => (
                    <Fragment key={resp?.value + i}>
                      <Option value={resp?.value} label={resp?.label}>
                        <div className="option-label-item">
                          <div className="option_wrap">
                            <span className="role_label">{resp?.label}</span>
                          </div>
                        </div>
                      </Option>
                    </Fragment>
                  )
                )}
            </Select>
            {optionalLabel && (
              <div className="optional-label">{optionalLabel}</div>
            )}
            {hintMessage && (
              <div
                className={warning ? "hint-label warning-label" : "hint-label"}
              >
                {hintMessage}
              </div>
            )}
          </>
        )}
      />
    </Form.Item>
  );
};
